import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Send, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";
import { Button } from "../components/ui/button";

export default function PerformanceMyFeedback() {
  const { t } = useTranslation();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("v1/performance/my-feedbacks/")
      .then((r) => setFeedbacks(r.data || []))
      .catch(() => toast.error(t("performance.feedback.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openFeedback = async (fb) => {
    setActiveFeedback(fb);
    setResponses({});
    try {
      const review = await api.get(`v1/performance/reviews/${fb.review_id}/`);
      const cycleResp = await api.get(`v1/performance/cycles/${review.data.cycle_id}/`);
      setCycle(cycleResp.data);
    } catch {
      setCycle(null);
    }
  };

  const setResponse = (questionId, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), question_id: questionId, [field]: value },
    }));
  };

  const handleSubmit = async () => {
    if (!activeFeedback) return;
    const questions = cycle?.questions || [];
    const required = questions.filter((q) => q.required);
    for (const q of required) {
      const resp = responses[q.id];
      if (q.type !== "text" && (resp?.score == null)) {
        toast.error(t("performance.feedback.scoreRequired", { question: q.text }));
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        responses: Object.values(responses).map((r) => ({
          question_id: r.question_id,
          score: r.score != null ? Number(r.score) : null,
          comment: r.comment || null,
        })),
      };
      await api.post(`v1/performance/feedback/${activeFeedback.id}/submit`, payload);
      toast.success(t("performance.feedback.submitted"));
      setActiveFeedback(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.feedback.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 rounded-full border-b-2" style={{ borderColor: "#0ea5e9" }} />
      </div>
    );
  }

  if (activeFeedback) {
    const questions = cycle?.questions || [];
    return (
      <FeedbackForm
        feedback={activeFeedback}
        t={t}
        questions={questions}
        ratingScale={cycle?.rating_scale || []}
        responses={responses}
        onSetResponse={setResponse}
        onSubmit={handleSubmit}
        submitting={submitting}
        onCancel={() => setActiveFeedback(null)}
      />
    );
  }

  const pending = feedbacks.filter((f) => !f.submitted_at);
  const done = feedbacks.filter((f) => f.submitted_at);

  return (
    <div className="space-y-4">
      <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("performance.feedback.title")}</h3>

      {pending.length === 0 && done.length === 0 && (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <MessageSquare className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--lm-ink-3)" }} />
          <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t("performance.feedback.empty")}</p>
          <p className="text-xs mt-1" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.feedback.emptyHint")}
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.feedback.toFill", { count: pending.length })}
          </p>
          {pending.map((fb) => (
            <FeedbackItem key={fb.id} feedback={fb} t={t} onOpen={() => openFeedback(fb)} />
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.feedback.doneCount", { count: done.length })}
          </p>
          {done.map((fb) => (
            <FeedbackItem key={fb.id} feedback={fb} t={t} done />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackItem({ feedback, t, onOpen, done }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-3 rounded-xl"
      style={{
        backgroundColor: "var(--lm-s2)",
        border: `1px solid ${done ? "var(--lm-border)" : "#0ea5e9"}`,
        opacity: done ? 0.75 : 1,
      }}
    >
      <div className="flex items-center gap-3">
        {done ? (
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: "#22c55e" }} />
        ) : (
          <MessageSquare className="h-4 w-4 flex-shrink-0" style={{ color: "#0ea5e9" }} />
        )}
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
            {t(`performance.feedback.roleLabels.${feedback.reviewer_role}`, { defaultValue: feedback.reviewer_role })}
            {feedback.is_anonymous && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--lm-border)", color: "var(--lm-ink-2)" }}>
                {t("performance.feedback.anonymous")}
              </span>
            )}
          </p>
          <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
            {done
              ? t("performance.feedback.submittedOn", { date: new Date(feedback.submitted_at).toLocaleDateString() })
              : t("performance.feedback.awaitingResponse")}
          </p>
        </div>
      </div>
      {!done && (
        <Button size="sm" onClick={onOpen}>
          {t("performance.feedback.fill")}
        </Button>
      )}
      {done && feedback.overall_score != null && (
        <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: "#22c55e" }}>
          <Star className="h-3.5 w-3.5" />
          {Number(feedback.overall_score).toFixed(1)}
        </div>
      )}
    </div>
  );
}

function FeedbackForm({ feedback, t, questions, ratingScale, responses, onSetResponse, onSubmit, submitting, onCancel }) {
  const scale = ratingScale.length > 0 ? ratingScale : [
    { value: 1, label: "1" }, { value: 2, label: "2" },
    { value: 3, label: "3" }, { value: 4, label: "4" }, { value: 5, label: "5" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>
            {t(`performance.feedback.roleLabels.${feedback.reviewer_role}`, { defaultValue: feedback.reviewer_role })}
          </h3>
          {feedback.is_anonymous && (
            <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-3)" }}>
              {t("performance.feedback.anonymousResponses")}
            </p>
          )}
        </div>
        <button onClick={onCancel} className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
          {t("performance.feedback.back")}
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t("performance.feedback.noQuestions")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((q) => (
            <QuestionField
              key={q.id}
              question={q}
              t={t}
              scale={scale}
              response={responses[q.id] || {}}
              onChange={(field, value) => onSetResponse(q.id, field, value)}
            />
          ))}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button size="sm" onClick={onSubmit} disabled={submitting || questions.length === 0}>
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {submitting ? t("performance.feedback.submitting") : t("performance.feedback.submitCta")}
        </Button>
      </div>
    </div>
  );
}

function QuestionField({ question, t, scale, response, onChange }) {
  const needsRating = question.type === "rating" || question.type === "both";
  const needsText = question.type === "text" || question.type === "both";

  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
          {question.text}
          {question.required && <span className="ml-1" style={{ color: "#ef4444" }}>*</span>}
        </p>
      </div>

      {needsRating && (
        <div className="flex flex-wrap gap-2">
          {scale.map((s) => {
            const selected = response.score === s.value;
            return (
              <button
                key={s.value}
                onClick={() => onChange("score", s.value)}
                className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border transition-all text-xs font-medium"
                style={{
                  backgroundColor: selected ? (s.color || "#0ea5e9") : "var(--lm-canvas)",
                  borderColor: selected ? (s.color || "#0ea5e9") : "var(--lm-border)",
                  color: selected ? "#ffffff" : "var(--lm-ink-2)",
                  minWidth: "52px",
                }}
              >
                <span className="text-base font-bold">{s.value}</span>
                <span className="text-center leading-tight" style={{ fontSize: "10px" }}>{s.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {needsText && (
        <textarea
          className="w-full rounded-md border px-3 py-2 text-sm resize-none"
          style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)", outline: "none" }}
          rows={3}
          placeholder={t("performance.feedback.commentPlaceholder")}
          value={response.comment || ""}
          onChange={(e) => onChange("comment", e.target.value)}
        />
      )}
    </div>
  );
}
